import React, { useEffect } from "react";

import useRequest from "@/helpers/useRequest";
import { apiPath } from "@/helpers/apiPath";
import { SearchInterface } from "@/helpers/pageHelpers/Common/interface";
import { UIStore } from "@/store/UIStore";
import ArticleTile from "@/components/FeedTiles/ArticleTile";
import { ArticleTileProps } from "@/helpers/pageHelpers/Article/interface";
import { SearchCategory } from "@/helpers/pageHelpers/Store/interface";
import PodcastCard from "@/components/PodcastCard";
import RecommendedPodcastTile from "@/components/FeedTiles/PodcastTile/RecommendedPodcastTile";
import AuthorTile from "@/components/AuthorTile";
import Typography from "@/components/Typography";
import SkeletonLoader from "@/components/SkeletonLoader";
import EmptyState from "@/components/EmptyState";

import styles from "./SearchAll.module.scss";

export const SearchAll: React.FC<SearchInterface> = ({ phrase }) => {
  const { data } = useRequest(apiPath.SEARCH_ALL({ searchQuery: phrase }));
  const articles = data?.articles?.items || [];
  const artists = data?.artists?.items || [];
  const podcasts = data?.podcasts?.items || [];
  const episodes = data?.episodes?.items || [];
  const videos = data?.videos?.items || [];
  const redactors = data?.redactors?.items || [];
  const isLoading = !data;
  const isEmpty =
    !articles.length &&
    !artists.length &&
    !podcasts.length &&
    !episodes.length &&
    !videos.length &&
    !redactors.length;

  useEffect(() => {
    UIStore.update((store) => {
      store.searchResults = data;
    });
  }, [data]);

  const switchContext = (context) => {
    window.scrollTo(0, 0);

    UIStore.update((store) => {
      store.searchCategory = context;
    });
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <SkeletonLoader />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {!!articles.length && (
        <div className={styles.category}>
          <Typography component="p" variant="h3" className={styles.heading}>
            Artykuły
            <span
              onClick={() => switchContext(SearchCategory.Articles)}
              className={styles.showMore}
            >
              Pokaż wszystko
            </span>
          </Typography>

          {articles.map((article) => {
            return (
              <ArticleTile
                key={article.slug}
                variant="small"
                {...(article as ArticleTileProps)}
              />
            );
          })}
        </div>
      )}

      {!!podcasts.length && (
        <div className={styles.category}>
          <Typography component="p" variant="h3" className={styles.heading}>
            Podcasty
            <span
              onClick={() => switchContext(SearchCategory.Podcasts)}
              className={styles.showMore}
            >
              Pokaż wszystko
            </span>
          </Typography>

          {podcasts.map((podcast) => {
            return (
              <PodcastCard
                className={styles.podcast}
                key={podcast.slug}
                id={podcast.id}
                slug={podcast.slug}
                title={podcast.title}
                picture={podcast.artwork}
                hosts={podcast.hosts}
                followed={podcast.followed}
              />
            );
          })}
        </div>
      )}

      {!!episodes.length && (
        <div className={styles.category}>
          <Typography component="p" variant="h3" className={styles.heading}>
            Epizody
            <span
              onClick={() => switchContext(SearchCategory.Episodes)}
              className={styles.showMore}
            >
              Pokaż wszystko
            </span>
          </Typography>

          {episodes.map((episode) => {
            return <RecommendedPodcastTile key={episode.slug} {...episode} />;
          })}
        </div>
      )}

      {!!redactors.length && (
        <div className={styles.category}>
          <Typography component="p" variant="h3" className={styles.heading}>
            Redaktorzy
            <span
              onClick={() => switchContext(SearchCategory.Authors)}
              className={styles.showMore}
            >
              Pokaż wszystko
            </span>
          </Typography>

          {redactors.map((redactor) => {
            return <AuthorTile key={redactor.slug} {...redactor} />;
          })}
        </div>
      )}

      {isEmpty && <EmptyState className={styles.emptyState} />}
    </div>
  );
};

export default SearchAll;
